import {makeAutoObservable, reaction} from "mobx";
import {NumberObservable} from "../../../observables/numberObservable";
import {TextObservable} from "../../../observables/textObservable";
import {EventPaidType} from "../../../../shared/enums/eventPaidType";
import {ObservableList} from "../../../observables/observableList";
import {KeyValue} from "../../../observables/keyValue";
import {EventCostAllocation} from "../../../../shared/enums/eventCostAllocation";

const minPrice = 1;
const maxPrice = 500;

export class EventPriceDetails {
  isPaidEvent: boolean;
  paidType: TextObservable;
  price: NumberObservable;
  costAllocation: ObservableList<KeyValue>;
  useValidation: boolean;
  isDirty: boolean;

  hasTransactionsFeature: boolean = false;

  constructor(
    price?: {amount: number | null; costAllocation?: string},
    useValidation?: boolean
  ) {
    const isPaidEvent = price?.amount !== 0;
    const priceValue = !!price ? price.amount : 0;
    const selectedPaidTypeValue =
      priceValue === 0
        ? EventPaidType.free
        : priceValue === null
        ? EventPaidType.ownExpense
        : EventPaidType.fixed;

    makeAutoObservable(this);
    this.isPaidEvent = isPaidEvent;
    this.paidType = new TextObservable(EventPaidType.free);
    this.price = new NumberObservable(
      priceValue ?? 0,
      minPrice,
      maxPrice,
      undefined,
      (value) => {
        return !!value && value >= minPrice && value <= maxPrice;
      }
    );

    this.costAllocation = new ObservableList<KeyValue>(
      [
        new KeyValue(
          "enhancedActivities",
          EventCostAllocation.EnhancedActivities
        ),
        new KeyValue("bar", EventCostAllocation.Bar),
        new KeyValue("fineDining", EventCostAllocation.FineDining)
      ],
      price?.costAllocation,
      (toValidate) => {
        if (this.price.number && this.price.number > 0) {
          return toValidate !== null;
        }
        // If no price is set, this is always valid.
        return true;
      }
    );

    if (selectedPaidTypeValue) {
      this.paidType.setText(selectedPaidTypeValue);
      this.price.setIsDisabled(this.paidType.text !== EventPaidType.fixed);
    }
    this.useValidation = useValidation ?? false;
    this.isDirty = false;
    reaction(
      () => this.price.number,
      () => {
        if (this.price.number === 0 || this.price.number === null) {
          this.costAllocation.setSelectedItem(null);
        }
        this.setIsDirty(true);
      }
    );
    reaction(
      () => this.costAllocation.selectedItem,
      () => {
        this.setIsDirty(true);
      }
    );
    reaction(
      () => this.isPaidEvent,
      () => {
        this.setIsDirty(true);
      }
    );
    reaction(
      () => this.paidType.text,
      () => {
        if (this.paidType.text !== EventPaidType.fixed) {
          this.price.setIsDisabled(true);
          this.price.setNumber(0);
        } else {
          this.price.setIsDisabled(false);
        }
        this.isPaidEvent = this.paidType.text !== EventPaidType.free;
        this.setIsDirty(true);
      }
    );
  }

  setHasTransactionsFeature = (value: boolean) => {
    this.hasTransactionsFeature = value;
  };

  setIsDirty = (value: boolean) => {
    this.isDirty = value;
  };

  get isValid() {
    return !this.useValidation || !this.isDirty || this.validate();
  }

  validate = () => {
    const priceIsValid =
      !this.isPaidEvent ||
      this.paidType.text === EventPaidType.ownExpense ||
      this.price.isValid;

    if (!this.hasTransactionsFeature) return priceIsValid;

    return priceIsValid && this.costAllocation.isValid;
  };
}
